import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { ButtonColor } from "@/components/Button/types";

import { CampaignStaleConflict } from "../utils/campaignRevision.util";

interface CampaignConflictModalProps {
	conflict: CampaignStaleConflict | null;
	onKeepMine: () => void;
	onTakeTheirs: () => void;
}

/**
 * The server refused the builder's save because a newer version of the campaign exists (another
 * tab, a teammate — PREC-4026). There is no safe default, so the user picks: overwrite the newer
 * version with this canvas, or drop this canvas and load the newer version. Deliberately not
 * dismissable — closing it would leave the canvas unsaved with no way to tell.
 */
export const CampaignConflictModal: FunctionComponent<CampaignConflictModalProps> = ({
	conflict,
	onKeepMine,
	onTakeTheirs,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "list.campaignBuilder" });

	if (!conflict) {
		return null;
	}

	return (
		<Modal
			handleSave={onKeepMine}
			handleSecondSave={onTakeTheirs}
			isOpened={true}
			secondSubmitButtonColor={ButtonColor.ACTION_SECONDARY}
			secondSubmitButtonText={t("conflictTakeTheirs")}
			size="sm"
			submitButtonText={t("conflictKeepMine")}
			title={t("conflictTitle")}
			zIndex={1400}
		>
			<div className="flex flex-col gap-3 text-sm text-gray-700">
				<p>{t("conflictBody")}</p>
				<p className="text-xs text-text-meta">{t("conflictHint")}</p>
			</div>
		</Modal>
	);
};
